import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Loader2,
  Printer,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { fetchFunctionPdf, runPdf } from '@/lib/pdf';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import {
  SUPPORT_ADDRESS_LINE1,
  SUPPORT_ADDRESS_LINE2,
  SUPPORT_EMAIL,
  SUPPORT_MAILTO_HREF,
  SUPPORT_PHONE,
  SUPPORT_PHONE_TOLL_FREE,
  SUPPORT_TEL_HREF,
  SUPPORT_TOLL_FREE_HREF,
} from '@/lib/constants';
import { useCustomer } from '@/hooks/useCustomer';
import { useGroupStatement } from '@/hooks/useGroupStatement';
import { useInvoices, type InvoiceRow } from '@/hooks/useInvoices';
import ChainStatement from './statements/ChainStatement';
import {
  AddressBlock,
  AgingBadge,
  AgingGrid,
  SummaryStat,
  bucketFor,
  daysPastDue,
  fmtDate,
  fmtLongDate,
  type BucketId,
} from './statements/parts';

export default function Statements() {
  const { data: invoices, isLoading, isError } = useInvoices();
  const { data: ctx } = useCustomer();
  // A chain/franchise master gets the merged by-store document instead: its
  // own account's invoices are only a fraction of what it actually pays.
  const { data: group, isLoading: groupLoading } = useGroupStatement();
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const docRef = useRef<HTMLDivElement>(null);

  // Download + Print both use the SERVER statement PDF (customer-statement-pdf)
  // — the exact document the DocuPost mailed statement uses — so mailed, saved,
  // and printed statements are identical. The on-screen document is view-only.
  const statementPdfName = () => `brix-statement-${new Date().toISOString().slice(0, 10)}.pdf`;

  async function handleDownloadPdf() {
    setDownloading(true);
    try {
      await runPdf('download', () => fetchFunctionPdf('customer-statement-pdf'), statementPdfName());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Statements] PDF download failed:', err);
      window.alert(`We couldn't download this statement. ${err instanceof Error ? err.message : ''}`);
    } finally {
      setDownloading(false);
    }
  }

  async function handlePrintPdf() {
    setPrinting(true);
    try {
      // The print tab opens before the fetch — see openPdfTab.
      await runPdf('print', () => fetchFunctionPdf('customer-statement-pdf'), statementPdfName());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Statements] PDF print failed:', err);
    } finally {
      setPrinting(false);
    }
  }

  // Stable "as of" date for the whole render pass.
  const today = useMemo(() => new Date(), []);

  const open = useMemo(
    () =>
      (invoices ?? [])
        .filter((r) => r.isOpen && r.balance > 0)
        .map((r) => ({ row: r, days: daysPastDue(r.dueDate, today) }))
        // Oldest-due first (largest days-past-due / earliest due date first).
        .sort(
          (a, b) =>
            new Date(a.row.dueDate).getTime() - new Date(b.row.dueDate).getTime(),
        ),
    [invoices, today],
  );

  const totalOpen = useMemo(
    () => open.reduce((s, o) => s + o.row.balance, 0),
    [open],
  );

  const aging = useMemo(() => {
    const sums: Record<BucketId, number> = {
      current: 0,
      d30: 0,
      d60: 0,
      d90: 0,
      d90plus: 0,
    };
    for (const o of open) {
      sums[bucketFor(o.days)] += o.row.balance;
    }
    return sums;
  }, [open]);

  if (isLoading || groupLoading) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-900">
        <Loader2 size={16} className="animate-spin mr-2" /> Loading statement…
      </div>
    );
  }

  if (group?.is_group && group.statement) {
    return (
      <ChainStatement statement={group.statement} footnote={group.separate_account_note} />
    );
  }

  const customerName = ctx?.customer.name ?? 'Your account';
  const primaryLocation = ctx?.locations[0] ?? null;
  const billToLines = primaryLocation
    ? [
        primaryLocation.addressLine1,
        primaryLocation.addressLine2,
        [
          [primaryLocation.city, primaryLocation.state]
            .filter(Boolean)
            .join(', ') || null,
          primaryLocation.zip,
        ]
          .filter(Boolean)
          .join(' ') || null,
      ].filter(Boolean as unknown as (v: string | null) => v is string)
    : [];

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-14 md:pb-0">
      <div data-print-doc className="px-6 md:px-8 pt-6 pb-10 max-w-3xl mx-auto">
        {/* Back + actions row (hidden in print) */}
        <div className="flex items-center justify-between mb-4 print:hidden">
          <Link
            to="/invoices"
            className="inline-flex items-center gap-1.5 text-sm text-slate-900 hover:text-slate-900 transition-colors cursor-pointer"
          >
            <ArrowLeft size={14} /> Back to invoices
          </Link>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleDownloadPdf()}
              disabled={downloading}
            >
              {downloading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Download size={13} />
              )}
              Download PDF
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void handlePrintPdf()}
              disabled={printing}
            >
              {printing ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Printer size={13} />
              )}
              Print
            </Button>
          </div>
        </div>

        {/* The statement "document" — dark-navy framed, deeper shadow, mirrors
            InvoiceDetail. print:shadow-none + print:border-0 strip chrome so
            the browser's Save-as-PDF produces a clean page. */}
        <div
          ref={docRef}
          className="rounded-2xl border-2 border-brix-primary-deep bg-brix-surface overflow-hidden shadow-card-deep print:shadow-none print:border-0"
        >
          {/* Dark navy accent strip */}
          <div className="h-3 bg-brix-primary-deep" aria-hidden />

          {/* Logo band + statement label */}
          <div className="px-6 md:px-8 pt-7 pb-6 border-b border-brix-border flex items-start justify-between gap-4">
            {/* Alameda Point Beverage Group lockup: large Brix logo · vertical
                rule · Alameda Soda. Inline (not BrixLogo) so the document brand
                matches the printed/mailed PDF exactly. */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-3">
                <img
                  src="/brand/brix-logo-full.png"
                  alt="Brix Beverage"
                  className="h-12 md:h-14 w-auto"
                />
                <span className="self-stretch w-px bg-brix-primary" aria-hidden />
                <img
                  src="/brand/alameda-soda-logo-navy.png"
                  alt="Alameda Soda"
                  className="h-7 md:h-8 w-auto"
                />
              </div>
              <p className="text-[10px] text-brix-muted">Alameda Point Beverage Group Inc.</p>
            </div>
            <div className="text-right">
              <p className="font-display text-2xl font-bold tracking-tight text-slate-900 leading-none">
                Account Statement
              </p>
              <p className="text-xs font-semibold text-slate-900 tracking-tight mt-2">
                As of {fmtLongDate(today)}
              </p>
            </div>
          </div>

          {/* From / Account */}
          <div className="px-6 md:px-8 py-5 grid grid-cols-1 md:grid-cols-2 gap-5 border-b border-brix-border">
            <AddressBlock
              label="From"
              name="Brix Beverage"
              lines={[
                'Hangar 25',
                SUPPORT_ADDRESS_LINE1,
                SUPPORT_ADDRESS_LINE2,
                `${SUPPORT_PHONE} · ${SUPPORT_PHONE_TOLL_FREE}`,
              ]}
            />
            <AddressBlock label="Account" name={customerName} lines={billToLines} />
          </div>

          {/* Summary band — open balance + open count + aging buckets */}
          <div className="px-6 md:px-8 py-5 border-b border-brix-border bg-brix-surface-alt/40">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
              <SummaryStat label="Total open balance" value={formatUsd(totalOpen)} emphasis />
              <SummaryStat
                label="Open invoices"
                value={String(open.length)}
              />
              <SummaryStat
                label="Past due"
                value={formatUsd(totalOpen - aging.current)}
                tone={totalOpen - aging.current > 0 ? 'warning' : undefined}
              />
            </div>

            <div className="text-[10px] tracking-tight text-slate-900 font-bold mb-2">
              Aging
            </div>
            <AgingGrid aging={aging} />
          </div>

          {/* Open-items table */}
          <div className="px-6 md:px-8 py-5 border-b border-brix-border">
            <div className="text-[10px] tracking-tight text-slate-900 font-bold mb-2">
              Open items
            </div>

            {open.length === 0 ? (
              <div className="rounded-lg border border-dashed border-brix-border px-4 py-8 bg-brix-surface-alt/40 flex flex-col items-center justify-center text-center gap-2">
                <CheckCircle2 size={22} className="text-emerald-700" />
                <p className="text-sm font-semibold text-slate-900">
                  You're all paid up — no open balance.
                </p>
                <p className="text-xs text-slate-500">
                  Nothing is currently due on this account.
                </p>
              </div>
            ) : (
              <OpenItemsTable items={open} totalOpen={totalOpen} />
            )}
          </div>

          {/* Remittance footer */}
          <div className="px-6 md:px-8 py-5 bg-brix-surface-alt/60 border-t border-brix-border">
            <div className="text-[10px] tracking-tight text-slate-900 font-bold mb-1.5">
              Remit payment to
            </div>
            <p className="text-sm font-semibold text-slate-900 tracking-tight">
              Brix Beverage
            </p>
            <p className="text-[11px] text-slate-900 tracking-tight">
              Hangar 25 · {SUPPORT_ADDRESS_LINE1} · {SUPPORT_ADDRESS_LINE2}
            </p>
            <p className="text-[11px] text-slate-900 tracking-tight mt-1">
              <a href={SUPPORT_TEL_HREF} className="text-brix-primary">
                {SUPPORT_PHONE}
              </a>{' '}
              ·{' '}
              <a href={SUPPORT_TOLL_FREE_HREF} className="text-brix-primary">
                {SUPPORT_PHONE_TOLL_FREE}
              </a>{' '}
              ·{' '}
              <a href={SUPPORT_MAILTO_HREF} className="text-brix-primary">
                {SUPPORT_EMAIL}
              </a>
            </p>
            <p className="text-[11px] text-slate-500 tracking-tight mt-2 print:hidden">
              Questions about a charge? Open any invoice to pay online or call us
              to pay by phone or check.
            </p>
          </div>
        </div>

        {/* Error degrade — match Invoices' silent-degrade philosophy but give a
            small note since a statement with no rows could otherwise look like
            "all paid up" when it's really a fetch failure. */}
        {isError && (
          <p className="text-center text-xs text-brix-muted mt-4 print:hidden">
            We couldn't load your latest balances just now. Refresh to try again.
          </p>
        )}
      </div>
    </div>
  );
}

function OpenItemsTable({
  items,
  totalOpen,
}: {
  items: { row: InvoiceRow; days: number }[];
  totalOpen: number;
}) {
  return (
    <div className="rounded-lg border border-brix-border overflow-hidden">
      {/* Header row */}
      <div className="hidden sm:grid grid-cols-[100px_1fr_100px_120px_120px] gap-3 px-3 py-2.5 text-[11px] font-semibold tracking-tight text-white bg-brix-primary-deep items-center">
        <div>Invoice date</div>
        <div>Invoice #</div>
        <div>Due date</div>
        <div className="text-right">Original</div>
        <div className="text-right">Balance due</div>
      </div>

      {items.map(({ row, days }) => {
        const overdue = days > 0;
        const to = `/invoices/${encodeURIComponent(row.qboInvoiceId)}`;
        const label = `#${row.docNumber ?? row.qboInvoiceId}`;
        return (
          <div key={row.qboInvoiceId}>
            {/* Mobile: stacked card */}
            <div className="sm:hidden px-3 py-3 border-b border-brix-border last:border-b-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={to}
                    className="text-sm font-bold font-mono tracking-tight text-brix-primary hover:underline"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {label}
                  </Link>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {fmtDate(row.txnDate)} · Due {fmtDate(row.dueDate)}
                  </div>
                  <AgingBadge days={days} className="mt-1" />
                </div>
                <div className="text-right shrink-0">
                  <div
                    className={cn(
                      'font-mono font-bold text-base tracking-tight',
                      overdue ? 'text-red-600' : 'text-slate-900',
                    )}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatUsd(row.balance)}
                  </div>
                  {row.balance !== row.totalAmount && (
                    <div className="font-mono text-[11px] text-slate-500 mt-0.5">
                      of {formatUsd(row.totalAmount)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop: aligned row */}
            <div className="hidden sm:grid grid-cols-[100px_1fr_100px_120px_120px] gap-3 px-3 py-2.5 items-center text-sm border-b border-brix-border last:border-b-0">
              <div className="text-slate-900 font-medium">{fmtDate(row.txnDate)}</div>
              <div className="min-w-0">
                <Link
                  to={to}
                  className="font-mono font-bold tracking-tight text-brix-primary hover:underline"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {label}
                </Link>
                <AgingBadge days={days} className="ml-2 align-middle" />
              </div>
              <div className="text-slate-900 font-medium">{fmtDate(row.dueDate)}</div>
              <div
                className="text-right font-mono text-slate-900 tracking-tight"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatUsd(row.totalAmount)}
              </div>
              <div
                className={cn(
                  'text-right font-mono font-bold tracking-tight',
                  overdue ? 'text-red-600' : 'text-slate-900',
                )}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatUsd(row.balance)}
              </div>
            </div>
          </div>
        );
      })}

      {/* Totals row */}
      <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[100px_1fr_100px_120px_120px] gap-3 px-3 py-3 items-center bg-brix-surface-alt/60 border-t border-brix-border">
        <div className="hidden sm:block" />
        <div className="hidden sm:block" />
        <div className="hidden sm:block" />
        <div className="text-xs font-bold tracking-tight text-slate-900 text-left sm:text-right">
          Total due
        </div>
        <div
          className="text-right font-mono font-bold text-base tracking-tight text-slate-900"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatUsd(totalOpen)}
        </div>
      </div>
    </div>
  );
}



